// client/recorder.ts
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import EventEmitter from "events";
import { IndexedDBBlobStore } from "./indexedDBStore.js";
var AudioRecorder = class extends EventEmitter {
  constructor({
    debug,
    mimeType,
    store = new IndexedDBBlobStore("recordings", "blobs"),
    saveInterval = 1e3
  } = {}) {
    super();
    this.hasStreamA = false;
    this.hasStreamB = false;
    this.context = new AudioContext();
    this.destination = new MediaStreamAudioDestinationNode(this.context);
    this.recordedChunks = [];
    globalThis.store = store;
    this.debug = debug;
    this.mimeType = mimeType ?? this.getSupportedMime();
    this.store = store;
    this.useStore = saveInterval !== -1;
    this.saveInterval = saveInterval;
    this.context.resume().catch((e) => console.error(e));
  }
  get recording() {
    return this.recorder?.state === "recording";
  }
  replaceStreamA(stream) {
    logger.debug({ mediaStreamID: this.sourceA?.mediaStream.id, streamID: stream.id }, "replacing streamA");
    const source = new MediaStreamAudioSourceNode(this.context, { mediaStream: stream });
    source.connect(this.destination);
    this.sourceA?.disconnect();
    this.sourceA = source;
    this.hasStreamA = true;
  }
  removeStreamA() {
    logger.debug("removing streamA");
    this.sourceA?.disconnect();
    this.hasStreamA = false;
  }
  replaceStreamB(stream) {
    logger.debug({ mediaStreamID: this.sourceB?.mediaStream.id, streamID: stream.id }, "replacing streamB");
    const source = new MediaStreamAudioSourceNode(this.context, { mediaStream: stream });
    source.connect(this.destination);
    if (this.sourceB)
      this.sourceB.disconnect();
    this.sourceB = source;
    this.hasStreamB = true;
  }
  removeStreamB() {
    logger.debug("removing streamB");
    this.sourceB?.disconnect();
    this.hasStreamB = false;
  }
  /**
   * @returns recordingID (generated or given)
   */
  async startRecording(recordingID = A.getGloballyUniqueID()) {
    logger.debug(
      { recordingID, sourceAStreamID: this.sourceA?.mediaStream.id, destinationStreamID: this.destination.stream.id },
      "starting recording"
    );
    await this.context.resume();
    if (this.recorder?.state === "recording")
      throw new Error("already recording");
    this.recorder = new MediaRecorder(this.destination.stream, { mimeType: this.mimeType });
    this.recordedChunks = [];
    const part = await this.store.getNextPart(recordingID);
    this.recorder.ondataavailable = (event) => this._handleBlobEvent(event, recordingID, part);
    this.recorder.start(this.useStore ? this.saveInterval : void 0);
    return recordingID;
  }
  pauseRecording() {
    if (this.recorder?.state === "recording")
      this.recorder.pause();
  }
  resumeRecording() {
    if (this.recorder?.state === "paused")
      this.recorder.resume();
  }
  async _handleBlobEvent(event, recordingID, part) {
    if (event.data.size > 0) {
      this.recordedChunks.push(event.data);
      if (this.useStore) {
        await this.store.storeBlob(recordingID, event.data, { index: this.recordedChunks.length - 1, part });
      }
      if (this.recorder?.state === "inactive") {
        await this._finishRecording(recordingID);
      }
    }
  }
  async _finishRecording(recordingID) {
    const files = [];
    if (this.useStore) {
      files.push(...await this.getFilesForRecording(recordingID));
    } else {
      files.push(
        new File(this.recordedChunks, this.destination.stream.id + "." + this.mimeType.split(";")[0]?.split("/")[1], {
          type: this.mimeType
        })
      );
    }
    this.emit("file", files);
    if (this.debug) {
      for (const file of files)
        this.downloadFile(file);
    }
  }
  async getFilesForRecording(recordingID) {
    const extension = "." + this.mimeType.split(";")[0]?.split("/")[1];
    const blobs = await this.store.getAllBlobs(recordingID);
    return blobs.sort((a, b) => a.part - b.part).reduce((grouped, blob) => {
      const last = grouped[grouped.length - 1];
      if (last && last[0]?.part === blob.part)
        last.push(blob);
      else
        grouped.push([blob]);
      return grouped;
    }, []).map(
      (blobs2) => new File(
        blobs2.sort((a, b) => a.index - b.index).map((b) => b.blob),
        `${this.destination.stream.id}_${blobs2[0]?.part}${extension}`,
        { type: this.mimeType }
      )
    );
  }
  async cleanupRecording(recordingID) {
    return this.store.removeAllBlobs(recordingID);
  }
  getSupportedMime() {
    let mimeType = "audio/webm; codecs=opus";
    if (!MediaRecorder.isTypeSupported(mimeType))
      mimeType = "audio/webm";
    if (!MediaRecorder.isTypeSupported(mimeType))
      mimeType = "audio/mp4";
    if (!MediaRecorder.isTypeSupported(mimeType))
      throw new Error(
        'Cannot create MediaRecorder, no wanted codecs are supported. Tried "audio/webm; codecs=opus", "audio/webm" and "audio/mp4". You can override the used mimeType by providing it as an option.'
      );
    return mimeType;
  }
  stopRecording(removeStreams) {
    logger.debug("stopping recording");
    if (this.recorder?.state !== "recording")
      throw new Error("can only stop recording if its running");
    this.recorder.stop();
    if (removeStreams) {
      this.sourceA?.disconnect();
      this.sourceB?.disconnect();
      this.hasStreamA = false;
      this.hasStreamB = false;
    }
  }
  async downloadFile(file) {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = this.destination.stream.id + ".webm";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
};
export {
  AudioRecorder
};
